import React, { useEffect, useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import * as YearEndService from '../../../../services/YearEndService';

const localDateTime = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const FilingSubmissionPanel = ({ companyId, processId, generatedEvidence, onStatusChange }) => {
    const [evidence, setEvidence] = useState(null);
    const [submittedAt, setSubmittedAt] = useState(localDateTime());
    const [receiptReference, setReceiptReference] = useState('');
    const [channel, setChannel] = useState('skatteverket_file_transfer');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        YearEndService.getFilingEvidence(companyId, processId)
            .then((resp) => {
                if (!live) return;
                setEvidence(resp?.data || null);
                onStatusChange(Boolean(resp?.data?.latestSubmission));
            })
            .catch(() => onStatusChange(false));
        return () => { live = false; };
    }, [companyId, processId, generatedEvidence, onStatusChange]);

    const artifact = generatedEvidence
        ? { artifact_id: generatedEvidence.artifactId, generated_at: generatedEvidence.generatedAt }
        : evidence?.latestArtifact;
    const confirmed = Boolean(evidence?.latestSubmission
        && evidence.latestSubmission.artifact_id === artifact?.artifact_id);

    const confirm = async () => {
        if (!artifact) return;
        setSaving(true);
        try {
            const resp = await YearEndService.confirmFilingSubmission(companyId, processId, {
                artifactId: artifact.artifact_id,
                channel,
                submittedAt,
                receiptReference: receiptReference.trim() || null,
            });
            setEvidence(resp?.data || null);
            onStatusChange(true);
            toast.success('Inlämningen är sparad och kopplad till denna SRU-version.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara inlämningsbekräftelsen.');
        } finally { setSaving(false); }
    };

    if (!artifact) {
        // En process vars filer genererades FÖRE inlämningsspårningen har
        // inga artefaktrader — utan förklaring blev "Markera som klart" en
        // permanent inaktiverad knapp (granskningsfynd 2026-08-09). Vägen
        // ur: generera om filerna, så registreras artefakten och panelen
        // ovan blir bekräftningsbar.
        return (
            <div className="alert alert-info mt-3" style={{ fontSize: '0.875rem' }}>
                Det finns ingen registrerad filversion för det här steget ännu.
                Generera (om) deklarationsfilerna ovan — då sparas en spårbar
                version som inlämningen kan bekräftas mot, och steget kan
                markeras som klart.
            </div>
        );
    }
    if (confirmed) {
        return (
            <div className="alert alert-success d-flex align-items-start gap-2 mt-3">
                <CheckCircle2 size={18} className="mt-1" />
                <div><strong>Inlämningen är dokumenterad.</strong><br />Den är kopplad till den senaste genererade SRU-versionen.</div>
            </div>
        );
    }
    return (
        <div className="card mt-3 border-primary">
            <div className="card-body">
                <h6 className="d-flex align-items-center gap-2"><ShieldCheck size={18} /> Bekräfta inlämningen</h6>
                <p className="text-muted small">Gör detta först efter att just dessa filer har lämnats till Skatteverket.</p>
                <div className="row g-2">
                    <div className="col-md-4">
                        <label className="form-label">Kanal</label>
                        <select className="form-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
                            <option value="skatteverket_file_transfer">Skatteverkets filöverföring</option>
                            <option value="manual_entry">Manuell registrering</option>
                            <option value="other">Annan kanal</option>
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Inlämnad</label>
                        <input type="datetime-local" className="form-control" value={submittedAt} onChange={(e) => setSubmittedAt(e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Kvittens/referens (om tillgänglig)</label>
                        <input className="form-control" value={receiptReference} onChange={(e) => setReceiptReference(e.target.value)} />
                    </div>
                </div>
                <button type="button" className="btn btn-primary btn-sm mt-3" onClick={confirm} disabled={saving || !submittedAt}>
                    {saving ? 'Sparar…' : 'Spara inlämningsbekräftelse'}
                </button>
            </div>
        </div>
    );
};

export default FilingSubmissionPanel;
